import { useState, type FormEvent } from "react";
import { ExternalLink, FilePlus2 } from "lucide-react";
import type { ContentItem } from "../domain/types";
import { Button, EmptyState, Field, Modal, PageHeader } from "../components/UI";
import { shortDate } from "../lib/format";
import { useWeSell } from "../state/WeSellProvider";

export function Content() {
  const { data, saving, addContent } = useWeSell();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [channel, setChannel] = useState<ContentItem["channel"]>("Instagram");
  const [type, setType] = useState<ContentItem["type"]>("reel");
  const [productIds, setProductIds] = useState<string[]>([]);
  if (!data) return null;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    await addContent({
      title,
      url,
      channel,
      type,
      productIds,
      publishedAt: new Date().toISOString(),
    });
    setOpen(false);
    setTitle("");
    setUrl("");
    setProductIds([]);
  };

  return (
    <>
      <PageHeader
        eyebrow="Rastreabilidade"
        title="Conteúdos"
        description="Ligue cada vídeo, post ou story aos produtos indicados."
        action={
          <Button onClick={() => setOpen(true)}>
            <FilePlus2 size={18} /> Novo conteúdo
          </Button>
        }
      />
      {data.contents.length ? (
        <div className="content-grid">
          {data.contents.map((item) => (
            <article className="panel content-card" key={item.id}>
              <div className="content-card__top">
                <span className="content-type">{item.type}</span>
                <small>{shortDate(item.publishedAt)}</small>
              </div>
              <h2>{item.title}</h2>
              <p>
                {item.channel} · {item.productIds.length} produto
                {item.productIds.length === 1 ? "" : "s"}
              </p>
              <div className="code-cloud">
                {item.productIds.map((id) => (
                  <span key={id}>
                    {data.products.find((product) => product.id === id)
                      ?.shortCode ?? "Sem código"}
                  </span>
                ))}
              </div>
              {item.url ? (
                <a
                  className="text-link"
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  Abrir conteúdo <ExternalLink size={15} />
                </a>
              ) : null}
            </article>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={<FilePlus2 size={28} />}
          title="Nenhum conteúdo registrado"
          description="Registre onde cada produto foi divulgado para enxergar o que realmente converte."
          action={
            <Button onClick={() => setOpen(true)}>Registrar conteúdo</Button>
          }
        />
      )}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Novo conteúdo"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button form="content-form" type="submit" loading={saving}>
              Salvar
            </Button>
          </>
        }
      >
        <form id="content-form" className="form-grid" onSubmit={submit}>
          <Field label="Título">
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              required
            />
          </Field>
          <div className="form-row">
            <Field label="Canal">
              <select
                value={channel}
                onChange={(event) =>
                  setChannel(event.target.value as ContentItem["channel"])
                }
              >
                {["Instagram", "TikTok", "YouTube", "Blog", "Outro"].map(
                  (value) => (
                    <option key={value}>{value}</option>
                  ),
                )}
              </select>
            </Field>
            <Field label="Formato">
              <select
                value={type}
                onChange={(event) =>
                  setType(event.target.value as ContentItem["type"])
                }
              >
                {["reel", "story", "post", "video", "blog", "outro"].map(
                  (value) => (
                    <option key={value}>{value}</option>
                  ),
                )}
              </select>
            </Field>
          </div>
          <Field label="URL publicada">
            <input
              type="url"
              value={url}
              onChange={(event) => setUrl(event.target.value)}
            />
          </Field>
          <fieldset className="product-picker">
            <legend>Produtos mencionados</legend>
            {data.products.map((product) => (
              <label key={product.id}>
                <input
                  type="checkbox"
                  checked={productIds.includes(product.id)}
                  onChange={(event) =>
                    setProductIds((current) =>
                      event.target.checked
                        ? [...current, product.id]
                        : current.filter((id) => id !== product.id),
                    )
                  }
                />
                <span>
                  <strong>{product.shortCode}</strong>
                  {product.name}
                </span>
              </label>
            ))}
          </fieldset>
        </form>
      </Modal>
    </>
  );
}
